/** Service Desk console: returns, repairs and warranty replacements (RMA) on the asset and the ticket (SD-S3-10). */
export const serviceRmas = {
  progress: "Return progress",
  staffOnly: "Staff only",
  withManufacturer: "With the manufacturer",
  stop: { outcome: "Outcome" },
  stepState: { done: "done", current: "current step", pending: "not yet" },
  status: {
    requested: "Requested",
    approved: "Approved",
    awaiting_return: "Awaiting return",
    received: "Received",
    inspected: "Inspected",
    credited: "Credited",
    replaced: "Replaced",
    repaired: "Repaired",
    rejected: "Rejected",
    closed: "Closed",
  },
  moveTo: {
    requested: "Back to requested",
    approved: "Approve",
    awaiting_return: "Ask for the item",
    received: "Mark received",
    inspected: "Record inspection",
    credited: "Credit",
    replaced: "Replace",
    repaired: "Repaired",
    rejected: "Reject",
    closed: "Close",
  },
  direction: { from_customer: "From the client", to_supplier: "To the manufacturer" },
  condition: { unused: "Unused", used: "Used", faulty: "Faulty" },
  resolution: { repair: "Repair", replace: "Replace", credit: "Credit" },
  actor: { staff: "Team", contact: "Client", system: "System" },
  event: {
    created: "RMA created",
    status_changed: "Status changed",
    updated: "Details updated",
    tracking_added: "Tracking reference added",
    supplier_linked: "Manufacturer RMA linked",
    supplier_status: "Manufacturer RMA moved",
  },
  errors: {
    not_allowed: "An RMA cannot make that move.",
    reason_required: "Give a reason for this move.",
    override_reason_required: "The hints say this return needs a second look: give the reason you approve it.",
    instructions_required: "Write the shipping instructions the client will read before asking for the item.",
    supplier_required: "Name the manufacturer the unit is sent to.",
    received_date_required: "Say the day the item arrived.",
    inspection_required: "Write what the inspection found.",
  },
  assetTab: { title: "Returns for this asset" },
  list: {
    label: "Returns",
    loading: "Loading returns",
    errorTitle: "Returns couldn't load",
    errorBody: "Check your connection and try again. Nothing was changed.",
    emptyTitle: "No returns",
    emptyBody: "A return, repair or warranty replacement appears here once one is started from a ticket.",
    rma: "RMA",
    item: "Item",
    ticket: "Ticket",
    status: "Status",
    updated: "Updated",
    open: "Open {{number}}",
  },
  create: {
    title: "Start an RMA",
    description: "A return, repair or warranty replacement for ticket {{ticket}}.",
    submit: "Create RMA",
    done: "{{number}} created",
    direction: "Who is it for",
    fromCustomerHint: "The client sends an item back to you.",
    toSupplierHint: "You send the unit to the manufacturer for a client's RMA.",
    asset: "Equipment",
    assetOther: "Not registered equipment",
    product: "Product name",
    productHint: "Needed when no equipment is chosen.",
    sku: "SKU",
    serial: "Serial number",
    quantity: "Quantity",
    resolution: "Wanted outcome",
    condition: "Condition",
    conditionUnknown: "Not known",
    reason: "What is wrong",
    instructions: "Shipping instructions for the client",
    instructionsHint: "The client reads these. You can write them now or when you ask for the item.",
    linked: "Serves the client's RMA",
    linkedNone: "Not linked",
    problems: { item: "Choose the equipment, or give a product name.", quantity: "Quantity is between 1 and 999." },
  },
  supplier: { name: "Manufacturer", reference: "Manufacturer's reference", notes: "Notes for the manufacturer" },
  eligibility: {
    title: "Does it qualify?",
    hintsOnly: "Hints only; you decide.",
    warrantyEnd: "Warranty ends",
    purchased: "Purchased",
    error: "The hints couldn't be worked out. You can still go on.",
    verdict: { eligible: "Looks eligible", review: "Needs a second look", unknown: "Not enough to tell" },
    reason: {
      in_warranty: "The equipment is in warranty.",
      warranty_expiring: "The warranty is about to end.",
      warranty_expired: "The warranty has ended.",
      warranty_unknown: "The warranty is not known.",
      no_asset: "No registered equipment is named.",
      within_return_window: "Inside the {{days}}-day return window.",
      outside_return_window: "Outside the {{days}}-day return window.",
      no_purchase_date: "There is no purchase date on record.",
      used_item: "The item is used, so the return window does not apply.",
    },
    source: { asset: "from the equipment record", order: "from the order history" },
  },
  drawer: {
    title: "{{number}}",
    titleLoading: "Return",
    loading: "Loading the RMA",
    clientSees: "The client reads: {{label}}",
    moves: "Move this RMA",
    item: "Item",
    product: "Product",
    ticket: "Ticket",
    sku: "SKU",
    serial: "Serial number",
    quantity: "Quantity",
    condition: "Condition",
    resolution: "Wanted outcome",
    tracking: "Tracking reference",
    noTracking: "Not added by the client yet",
    servesRma: "Back-office RMA for {{number}}.",
    supplierRmas: "Manufacturer RMAs: {{numbers}}.",
    receivedOnLine: "Received on {{date}}.",
    details: "Shipping and inspection",
    instructions: "Shipping instructions",
    instructionsHint: "The client reads these.",
    staffOnlyHint: "The client never sees these fields.",
    receivedOn: "Received on",
    fee: "Restocking fee",
    inspection: "Inspection result",
    save: "Save changes",
    saved: "{{number}} saved",
    timeline: "Timeline",
    timelineEmpty: "Nothing recorded yet.",
    timelineError: "The timeline couldn't load.",
  },
  status_dialog: {
    title: "{{number}}: {{status}}",
    confirm: "Move to {{status}}",
    done: "{{number}} is now {{status}}",
    required: "This is needed for the move.",
    review: "The hints say this return needs a second look. Say why you approve it anyway.",
    reason: "Reason",
    overrideReason: "Why you approve it anyway",
    instructions: "Shipping instructions for the client",
    instructionsHint: "Where and how to send the item. The client reads this when asked to send it.",
    supplier: "Manufacturer",
    receivedOn: "Day the item arrived",
    inspection: "What the inspection found",
    fee: "Restocking fee (optional)",
    feeHint: "Money in pesos with up to two decimals. It is informational.",
    feeProblem: "Use a number such as 250 or 250.50.",
    nothingNeeded: "Nothing more is needed. The client is told in their own words.",
    body: {
      requested: "Put the RMA back to requested.",
      approved: "Accept the return request.",
      awaiting_return: "The client is told where to send the item.",
      received: "Record that the item arrived.",
      inspected: "Record what the inspection found.",
      credited: "Credit the client.",
      replaced: "Replace the item.",
      repaired: "Mark it repaired.",
      rejected: "Turn the request down; the reason is kept.",
      closed: "End the RMA. It can no longer change.",
    },
  },
} as const;
